/**
 * WP8 · BK-Profil — Vollbild-Edit-View (Betreuungskraft)
 *
 * Klon des PB-Musters (`pflege/profile/PbProfileEdit.tsx`) mit BK-Feldsatz:
 * lädt profile_options, rendert die BK-Sections mit Sticky-Sprung-Nav,
 * Scroll-Spy und Sticky-Save-Bar. Speichern persistiert mit Field-Ownership-
 * Split parallel an
 *   PUT /v1/admin/user/{id}      (User-Felder)
 *   PUT /v1/admin/profile/{id}   (Profil-Felder)
 *
 * Bewusst NICHT übernommen: Phasen-Daten-Card, Familienkonto-Kopfblock,
 * Preis-/Vertragspfad (PricePB) und WP5-Section-Permissions (die Permission-
 * Keys `pb.section.*` sind PB-spezifisch; BK-Pendants existieren im Backend
 * noch nicht — dokumentierter offener Punkt).
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { formatTime } from '../../../lib/dateFormat';
import {
  v1ProfileOptions, v1Users, v1Profiles,
  type V1ProfileOption, type V1User, type V1Profile, primaryProfile,
} from '../../../lib/v1';
import { BK_SECTIONS_NAV, BkFormSections, type BkFormState, type BkPlTexts } from './sections';
import { validateBkProfile } from './validation';

interface Props {
  user: V1User | null;
  onClose: () => void;
  /** Wird nach erfolgreichem Speichern mit dem aktualisierten User gerufen. */
  onSaved?: (u: V1User) => void;
  dark: boolean;
  /** true → Breadcrumb/Chrome entfällt (der äußere BkDetailPage-Wrapper trägt sie). */
  embedded?: boolean;
}

/* ─── Field-Ownership: welcher Form-Key in welche Tabelle schreibt ──────── */
export const BK_USER_FIELDS = [
  // 'konto' (Konto-Status) wird seit 23.10.2026 bewusst nicht mehr gesendet.
  'status', 'badge', 'impfstatus', 'pause_von', 'pause_bis',
  'experience', 'german_skills',
  'email', 'mobile', 'telefon', 'strasse', 'hausnummer', 'plz', 'ort', 'land',
  'kontoinhaber', 'bankname', 'iban', 'description',
] as const;

export const BK_PROFILE_FIELDS = [
  'anrede', 'titel', 'vorname', 'nachname', 'geburtsdatum', 'nationalitaet',
  'groesse', 'gewicht', 'familienstand', 'raucher',
  'krankenkasse', 'krankenkasse_typ', 'kranken_vnr', 'sozial_vnr', 'steuer_id', 'lbnr',
  'qualifikation',
  'bk_wohnen', 'ausgepraegtes_familienleben', 'raucherhaushalt_akzeptiert', 'bk_aktivitaeten', 'bk_anrede', 'bk_deutsch', 'bk_fuehrerschein',
  'hobbys', 'umfeld', 'sonstiges',
  // Einsatz-Parameter (Teilmenge des Pflege-Katalogs, siehe Section 06)
  'wohnsituation', 'wohnort', 'haustiere', 'haustiere_welche', 'charaktereigenschaften',
  // Pflege-Parameter (CRM 1.0 rendert den Parameter-Block auch für BKs)
  // Batch-12: sgb5_bk (Checkbox 06 Einsatzparameter), beweglichkeit = Multi
  'sgb5_bk',
  'pflegegrad', 'beweglichkeit', 'einschraenkungen', 'sinneswahrnehmung',
  'geisteszustand', 'schlafverhalten',
  'grundpflege', 'hygiene', 'ankleiden', 'essen', 'urinkontrolle', 'stuhlkontrolle',
  'wohnzustand', 'einkaufsmoeglichkeiten',
] as const;

/** Teilmenge des Form-States → Plain-Objekt (undefined wird ausgelassen). */
function pickPayload(form: BkFormState, keys: readonly (keyof BkFormState)[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const v = form[k];
    if (v === undefined) continue;
    out[k] = v;
  }
  return out;
}

/**
 * PL-Freitexte aus `user.translations` (profile_field_translations) auflösen.
 * Unterstützt beide Serialisierungen:
 *   - CRM 2.0: { profile_id, language, translation: { hobbys, … } }
 *   - CRM 1.0-Legacy: { id: <profile_id>, hobbys, umfeld, sonstiges }
 */
export function bkPlTextsFor(user: V1User | null, profileId: number | null): BkPlTexts {
  const rows = (user as Record<string, unknown> | null)?.translations;
  if (!Array.isArray(rows)) return {};
  const out: BkPlTexts = {};
  for (const raw of rows) {
    if (raw == null || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const pid = r.profile_id ?? r.id;
    if (profileId != null && pid != null && Number(pid) !== Number(profileId)) continue;
    if (typeof r.language === 'string' && r.language !== 'pl') continue;
    const src = (r.translation != null && typeof r.translation === 'object')
      ? r.translation as Record<string, unknown>
      : r;
    for (const key of ['hobbys', 'umfeld', 'sonstiges'] as const) {
      const v = src[key];
      if (typeof v === 'string' && v.trim() && out[key] === undefined) out[key] = v;
    }
  }
  return out;
}

/* Map V1User + V1Profile → flacher BkFormState. */
export function bkUserToForm(user: V1User | null): BkFormState {
  if (!user) return {};
  const p: V1Profile | null = primaryProfile(user);
  const safeStr = (v: unknown): string | undefined => (v == null ? undefined : String(v));
  const safeArr = (v: unknown): string[] | undefined => {
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === 'string' && v.startsWith('[')) {
      try { const parsed = JSON.parse(v); if (Array.isArray(parsed)) return parsed.map(String); } catch { /* ignore */ }
    }
    return undefined;
  };
  const listOrSingle = (v: unknown): string[] => {
    const arr = safeArr(v);
    if (arr !== undefined) return arr;
    const s = safeStr(v);
    return s ? [s] : [];
  };
  return {
    // users
    status: safeStr(user.status),
    konto: safeStr(user.konto),
    badge: safeStr(user.badge as unknown),
    impfstatus: safeStr(user.impfstatus as unknown),
    rating: typeof user.rating === 'number' ? user.rating : Number(user.rating ?? 0),
    // Datums-Casts kommen ggf. als ISO-Timestamp — auf YYYY-MM-DD kürzen
    pause_von: (safeStr(user.pause_von as unknown) ?? '').slice(0, 10),
    pause_bis: (safeStr(user.pause_bis as unknown) ?? '').slice(0, 10),
    experience: safeStr(user.experience as unknown),
    german_skills: safeStr(user.german_skills as unknown),
    email: safeStr(user.email),
    mobile: safeStr(user.mobile),
    telefon: safeStr(user.telefon as unknown),
    strasse: safeStr(user.strasse as unknown),
    hausnummer: safeStr(user.hausnummer as unknown),
    plz: safeStr(user.plz as unknown),
    ort: safeStr(user.ort as unknown),
    land: safeStr(user.land as unknown),
    kontoinhaber: safeStr(user.kontoinhaber as unknown),
    bankname: safeStr(user.bankname as unknown),
    iban: safeStr(user.iban as unknown),
    description: safeStr(user.description as unknown),
    // profiles
    anrede: safeStr(p?.anrede),
    titel: safeStr(p?.titel),
    vorname: safeStr(p?.vorname),
    nachname: safeStr(p?.nachname),
    geburtsdatum: safeStr(p?.geburtsdatum),
    nationalitaet: safeStr(p?.nationalitaet as unknown),
    groesse: safeStr(p?.groesse as unknown),
    gewicht: safeStr(p?.gewicht as unknown),
    familienstand: safeStr(p?.familienstand as unknown),
    raucher: safeStr(p?.raucher as unknown),
    krankenkasse: safeStr(p?.krankenkasse as unknown),
    krankenkasse_typ: safeStr(p?.krankenkasse_typ as unknown),
    kranken_vnr: safeStr(p?.kranken_vnr as unknown),
    sozial_vnr: safeStr(p?.sozial_vnr as unknown),
    steuer_id: safeStr(p?.steuer_id as unknown),
    lbnr: safeStr(p?.lbnr as unknown),
    qualifikation: safeStr(p?.qualifikation as unknown),
    bk_wohnen: safeArr(p?.bk_wohnen as unknown),
    ausgepraegtes_familienleben: Boolean((p as Record<string, unknown> | null)?.ausgepraegtes_familienleben),
    raucherhaushalt_akzeptiert: Boolean((p as Record<string, unknown> | null)?.raucherhaushalt_akzeptiert),
    bk_aktivitaeten: safeArr(p?.bk_aktivitaeten as unknown),
    bk_anrede: safeArr(p?.bk_anrede as unknown),
    bk_deutsch: safeStr(p?.bk_deutsch as unknown),
    bk_fuehrerschein: safeStr(p?.bk_fuehrerschein as unknown),
    hobbys: safeStr(p?.hobbys as unknown),
    umfeld: safeStr(p?.umfeld as unknown),
    sonstiges: safeStr(p?.sonstiges as unknown),
    // Einsatz-Parameter
    // Mehrfachauswahl (CRM 1.0 speicherte bei BKs bereits Listen, z.B.
    // [70,71,72]); Einzelwerte aus dem Einzel-Select → Ein-Element-Liste.
    wohnsituation: listOrSingle(p?.wohnsituation as unknown),
    wohnort: listOrSingle(p?.wohnort as unknown),
    haustiere: safeStr(p?.haustiere as unknown),
    haustiere_welche: safeArr(p?.haustiere_welche as unknown),
    charaktereigenschaften: safeArr(p?.charaktereigenschaften as unknown),
    // Pflege-Parameter
    pflegegrad: safeStr(p?.pflegegrad as unknown),
    // Batch-12: Beweglichkeit ist bei BKs eine Mehrfachauswahl. Gespeichert
    // wird ein JSON-Array-String; Legacy-Zeilen können noch einen einzelnen
    // Wert (Plain-String) tragen → als Ein-Element-Array laden.
    beweglichkeit: (() => {
      const v = p?.beweglichkeit as unknown;
      const arr = safeArr(v);
      if (arr !== undefined) return arr;
      const s = safeStr(v);
      return s ? [s] : [];
    })(),
    // Batch-12: SGB5-Checkbox (06 Einsatzparameter)
    sgb5_bk: Boolean((p as Record<string, unknown> | null)?.sgb5_bk),
    einschraenkungen: safeArr(p?.einschraenkungen as unknown),
    sinneswahrnehmung: safeArr(p?.sinneswahrnehmung as unknown),
    geisteszustand: safeStr(p?.geisteszustand as unknown),
    schlafverhalten: safeStr(p?.schlafverhalten as unknown),
    grundpflege: safeStr(p?.grundpflege as unknown),
    hygiene: safeStr(p?.hygiene as unknown),
    ankleiden: safeStr(p?.ankleiden as unknown),
    essen: safeStr(p?.essen as unknown),
    urinkontrolle: safeStr(p?.urinkontrolle as unknown),
    stuhlkontrolle: safeStr(p?.stuhlkontrolle as unknown),
    wohnzustand: safeArr(p?.wohnzustand as unknown),
    einkaufsmoeglichkeiten: safeStr(p?.einkaufsmoeglichkeiten as unknown),
  };
}

export function BkProfileEdit({ user, onClose, onSaved, dark, embedded = false }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  const [f, setF] = useState<BkFormState>(() => bkUserToForm(user));
  const [opts, setOpts] = useState<Record<string, V1ProfileOption[]> | null>(null);
  const [optsError, setOptsError] = useState<string | null>(null);
  const [active, setActive] = useState<string>('stammdaten');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Array<keyof BkFormState>>([]);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const jumpingRef = useRef(false);
  const profileId = user ? primaryProfile(user)?.id ?? null : null;
  const plTexts = useMemo(() => bkPlTextsFor(user, profileId), [user, profileId]);

  // profile_options laden (DB-echte Dropdown-Werte; Fallback siehe options.ts)
  useEffect(() => {
    let alive = true;
    v1ProfileOptions.grouped()
      .then(grouped => { if (alive) setOpts(grouped); })
      .catch(e => { if (alive) setOptsError(e instanceof Error ? e.message : tr('pflege.edit.error_options')); });
    return () => { alive = false; };
  }, []);

  const set = useCallback(<K extends keyof BkFormState>(key: K, val: BkFormState[K]) => {
    setF(prev => ({ ...prev, [key]: val }));
  }, []);

  // Scroll-Container: Desktop = innerer mainRef; Mobile = outer <main.app-main>
  const getScrollContainer = useCallback((): HTMLElement | null => {
    if (!isMobile) return mainRef.current;
    return document.querySelector('main.app-main');
  }, [isMobile]);

  const STICKY_OFFSET = isMobile ? 70 : 90;

  // Scroll-Spy über alle Section-Ids
  useEffect(() => {
    const main = getScrollContainer();
    if (!main) return;
    const onScroll = () => {
      if (jumpingRef.current) return;
      const ids = BK_SECTIONS_NAV.flatMap(g => g.items.map(i => i.id));
      const mTop = main.getBoundingClientRect().top;
      let current: string = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - mTop <= STICKY_OFFSET) current = id;
      }
      setActive(current);
    };
    main.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => main.removeEventListener('scroll', onScroll);
  }, [getScrollContainer, STICKY_OFFSET]);

  const jump = (id: string) => {
    const main = getScrollContainer();
    const el = document.getElementById(id);
    if (!main || !el) return;
    jumpingRef.current = true;
    setActive(id);
    const top = el.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop - STICKY_OFFSET + 12;
    // jsdom hat kein Element.scrollTo — defensiv prüfen (Unit-Tests).
    if (typeof main.scrollTo === 'function') main.scrollTo({ top, behavior: 'smooth' });
    setTimeout(() => { jumpingRef.current = false; }, 600);

    if (isMobile) {
      const pill = document.querySelector<HTMLElement>(`[data-bk-nav="${id}"]`);
      const nav = pill?.parentElement;
      if (pill && nav) {
        const pRect = pill.getBoundingClientRect();
        const nRect = nav.getBoundingClientRect();
        const targetLeft = pRect.left - nRect.left + nav.scrollLeft - (nRect.width - pRect.width) / 2;
        nav.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
      }
    }
  };

  const save = async () => {
    if (saving) return;
    const vr = validateBkProfile(f);
    if (vr.missing.length > 0) {
      setValidationErrors(vr.missing);
      setSaveError(tr('betreuung.edit.validation_missing', { fields: vr.missing.join(', ') }));
      if (vr.firstSection) jump(vr.firstSection);
      return;
    }
    setValidationErrors([]);
    if (!user?.id) {
      // „Neue BK"-Flow bräuchte POST /admin/user — noch nicht implementiert.
      setSaveError(tr('pflege.edit.new_not_impl'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const userPayload = pickPayload(f, BK_USER_FIELDS as readonly (keyof BkFormState)[]);
      const profilePayload = pickPayload(f, BK_PROFILE_FIELDS as readonly (keyof BkFormState)[]);
      // Batch-12: beweglichkeit (Multi) als JSON-Array-String senden — die
      // profiles.beweglichkeit-Spalte bleibt ein String-Feld (Legacy-kompatibel).
      if (Array.isArray(profilePayload.beweglichkeit)) {
        profilePayload.beweglichkeit = JSON.stringify(profilePayload.beweglichkeit);
      }

      // PUT user (immer) + PUT profile (nur wenn Profil-Id bekannt) — sequenziell
      // wie im PB-Muster, damit Backend-Nebenwirkungen vor dem Merge greifen.
      await v1Users.update(user.id, userPayload);
      if (profileId != null && Object.keys(profilePayload).length > 0) {
        await v1Profiles.update(profileId, profilePayload);
      }

      // Vollständigen Stand neu laden (Muster PbProfileEdit): die PUT-Antworten
      // sind nackte Models ohne translations/prices/boni — damit weitergearbeitet,
      // wirkten PL-Freitexte und Vertrags-/Bonus-Historie nach dem Save leer.
      const fresh = await v1Users.get(user.id);
      onSaved?.(fresh);
      setF(bkUserToForm(fresh));

      setSavedAt(formatTime(new Date()));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setSaving(false);
    }
  };
  const reset = () => { setF(bkUserToForm(user)); setValidationErrors([]); };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: isMobile ? 'auto' : '100%',
      minHeight: 0,
    }}>

      {/* Standalone-Chrome (Breadcrumb + Zurück) — embedded trägt der Wrapper. */}
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, flexShrink: 0 }}>
          <button onClick={onClose} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 8,
            padding: '6px 12px', cursor: 'pointer', color: t.text,
            fontSize: 13, fontFamily: 'Outfit,sans-serif',
          }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>‹</span> {tr('pflege.detail.back')}
          </button>
          <div style={{ fontSize: 13, color: t.muted }}>
            {tr('nav.menu.betreuung')} <span style={{ margin: '0 6px' }}>/</span>
            <span style={{ color: t.heading, fontWeight: 600 }}>{tr('pflege.edit.breadcrumb_edit')}</span>
          </div>
        </div>
      )}

      {optsError && (
        <Card dark={dark} style={{ padding: 12, marginBottom: 12, borderColor: '#f04438' }}>
          <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>
            {tr('pflege.edit.error_options_card')} ({optsError})
          </div>
        </Card>
      )}

      {/* Scroll-Bereich — Desktop scrollt intern, Mobile über das outer <main>. */}
      <div ref={mainRef} style={{
        flex: isMobile ? undefined : 1,
        overflowY: isMobile ? 'visible' : 'auto',
        minHeight: 0,
        paddingRight: isMobile ? 0 : 4,
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', paddingBottom: 24 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '220px 1fr',
            gap: isMobile ? 14 : 28,
            marginTop: embedded ? 0 : 20, alignItems: 'start',
          }}>
            <BkSectionNav active={active} onJump={jump} dark={dark} isMobile={isMobile} />
            <BkFormSections f={f} set={set} opts={opts} dark={dark} invalidFields={validationErrors} plTexts={plTexts} user={user} />
          </div>
        </div>
      </div>

      {/* Sticky Save-Bar (Muster PbProfileEdit). */}
      <div style={{
        borderTop: `1px solid ${t.border}`, background: t.card,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        gap: isMobile ? 10 : 0,
        padding: isMobile ? '12px 12px' : '14px 8px',
        flexShrink: 0, marginTop: 14,
      }}>
        <div style={{ fontSize: 12.5, color: saveError ? '#b42318' : t.muted }}>
          {saveError
            ? tr('pflege.edit.error_prefix', { msg: saveError })
            : saving
              ? tr('pflege.edit.saving')
              : savedAt
                ? tr('pflege.edit.saved_at', { time: savedAt })
                : tr('pflege.edit.save_hint')}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            variant="secondary" dark={dark} onClick={reset}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >{tr('pflege.edit.reset')}</Button>
          <Button
            variant="primary" dark={dark} onClick={save} icon={<Icon.Check c="#fff" />}
            style={isMobile ? { flex: 1, justifyContent: 'center' } : undefined}
          >
            {saving ? tr('pflege.edit.saving') : tr('pflege.edit.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── Sticky Section-Nav mit Scroll-Spy (BK-Variante ohne Perms) ─────────── */
function BkSectionNav({
  active, onJump, dark, isMobile = false,
}: {
  active: string;
  onJump: (id: string) => void;
  dark: boolean;
  isMobile?: boolean;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  // Mobile: horizontale Pill-Leiste (Muster PbProfileEdit.SectionNav)
  const navScrollRef = useRef<HTMLElement | null>(null);
  const [showRightFade, setShowRightFade] = useState(false);
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const update = () => setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [isMobile, active]);

  if (isMobile) {
    type NavItem = { id: string; num: string; labelKey: string };
    const flat: NavItem[] = BK_SECTIONS_NAV.flatMap(grp => grp.items as readonly NavItem[]);
    return (
      <div style={{ position: 'sticky', top: 0, zIndex: 10, borderRadius: 10 }}>
        <nav
          ref={navScrollRef as React.RefObject<HTMLElement>}
          style={{
            display: 'flex', gap: 6, padding: 4,
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
            boxShadow: dark ? 'none' : '0 2px 6px rgba(16,24,40,0.08)',
          }}>
          {flat.map(it => {
            const on = active === it.id;
            return (
              <button
                key={it.id}
                type="button"
                data-bk-nav={it.id}
                onClick={() => onJump(it.id)}
                style={{
                  border: 'none', cursor: 'pointer',
                  padding: '8px 12px', borderRadius: 8,
                  background: on ? BRAND_LIGHT : 'transparent',
                  color: on ? BRAND_DARK : t.text,
                  fontSize: 12.5, fontWeight: on ? 600 : 500,
                  fontFamily: 'Outfit,sans-serif',
                  flexShrink: 0, whiteSpace: 'nowrap', minHeight: 38,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.15s, color 0.15s',
                }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: on ? BRAND : t.muted }}>{it.num}</span>
                {tr(it.labelKey)}
              </button>
            );
          })}
        </nav>
        {showRightFade && (
          <div aria-hidden style={{
            position: 'absolute', top: 1, right: 1, bottom: 1, width: 44,
            borderRadius: '0 10px 10px 0', pointerEvents: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 7,
            background: `linear-gradient(to right, transparent, ${t.card} 72%)`,
          }}>
            <span style={{ color: BRAND, fontSize: 17, fontWeight: 700, lineHeight: 1 }}>›</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <nav style={{
      position: 'sticky', top: 0, alignSelf: 'start',
      display: 'flex', flexDirection: 'column', gap: 18,
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 80px)',
    }}>
      {BK_SECTIONS_NAV.map(grp => (
        <div key={grp.groupKey}>
          <div style={{
            fontSize: 10.5, fontWeight: 700, color: t.muted,
            textTransform: 'uppercase', letterSpacing: '0.07em',
            marginBottom: 8, paddingLeft: 12,
          }}>{tr(grp.groupKey)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {grp.items.map(it => {
              const on = active === it.id;
              return (
                <button key={it.id} type="button" onClick={() => onJump(it.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, border: 'none', cursor: 'pointer',
                  borderRadius: 8, padding: '8px 12px', textAlign: 'left', fontFamily: 'Outfit,sans-serif',
                  background: on ? BRAND_LIGHT : 'transparent', color: on ? BRAND_DARK : t.text,
                  fontSize: 13, fontWeight: on ? 600 : 500, transition: 'all 0.12s',
                }}
                  onMouseEnter={e => { if (!on) (e.currentTarget as HTMLButtonElement).style.background = t.hover; }}
                  onMouseLeave={e => { if (!on) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: on ? BRAND : t.muted, width: 16, flexShrink: 0 }}>{it.num}</span>
                  {tr(it.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
